"use client";

import { useEffect, useRef, useState } from "react";

interface AppErrorNoticeProps {
  description?: string;
  durationMs?: number;
  title?: string;
}

export default function AppErrorNotice({
  description = "لطفاً چند لحظه دیگر دوباره امتحان کنید.",
  durationMs = 5000,
  title = "برنامه با خطا روبه‌رو شد",
}: AppErrorNoticeProps) {
  const reportedRef = useRef(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), durationMs);

    return () => window.clearTimeout(timer);
  }, [durationMs]);

  useEffect(() => {
    if (reportedRef.current) return;

    reportedRef.current = true;
    reportApplicationNotice(title, description);
  }, [description, title]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-5 z-[300] flex justify-center px-4"
      dir="rtl"
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-md border border-destructive/20 bg-background/95 px-4 py-3 text-right shadow-xl backdrop-blur">
        <div className="flex items-start gap-3">
          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-destructive" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {description}
            </p>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-destructive/10">
          <div
            className="h-full bg-destructive"
            style={{
              animation: `app-error-notice-progress ${durationMs}ms linear forwards`,
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes app-error-notice-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

function reportApplicationNotice(title: string, description: string) {
  const now = new Date();

  void fetch("/api/log/backend-error/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: createFrontendNoticeId(),
      time: now.toISOString(),
      service: "frontend",
      method: "CLIENT",
      url: window.location.href,
      status: 0,
      statusText: "Application Notice",
      durationMs: 0,
      path: window.location.pathname,
      message: title,
      request: {
        headers: {
          "X-Agent": navigator.userAgent,
          "X-User-ID": readCookie("user-id"),
          "X-Device": readCookie("device"),
          "X-Current-Path": window.location.pathname,
          "X-Current-Search": window.location.search,
          "X-Occurred-At": now.toISOString(),
        },
      },
      error: {
        name: "ApplicationNotice",
        message: description,
      },
    }),
  }).catch(() => undefined);
}

function createFrontendNoticeId() {
  const stamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();

  return `FE-${stamp}-${random}`;
}

function readCookie(name: string) {
  return (
    document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(`${name}=`))
      ?.split("=")
      .slice(1)
      .join("=") || ""
  );
}
